import classNames from 'classnames'
import React, { PropsWithChildren, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useProductReadContext } from '@context/ProductContext'

import { Body1, Heading6 } from '@ui/Typography'
import { ChevronIcon } from '@ui/icons/Chevron.icon'

import { TickmillCompaniesEnum } from '@utils/companyName.utils'

import { useScrollToElementIds } from '@hooks/useScrollToElementIds'

import styles from './ProfessionalClassificationTestForm.module.scss'

export const ClassificationText = (): JSX.Element => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const { isDefaultCFDProductType } = useProductReadContext()

  const isProductTypeCFD = isDefaultCFDProductType()

  if (isProductTypeCFD) {
    return (
      <>
        <Accordion
          title={t('Profile.Benefits of becoming a professional client')}
          id={'professional-client-benefits-accordion'}
        >
          {[
            {
              name: t('Profile.Segregated Account'),
              text: t('Profile.We will continue to segregate your funds'),
            },
            {
              name: t('Profile.Flexible Leverage'),
              text: t(
                'Profile.You will continue to benefit from the flexible leverage on our FX and CFD products'
              ),
            },
          ].map(({ name, text }, i) => {
            return (
              <div key={i}>
                <Body1 isParagraph>{`${i + 1}. ${name}`}</Body1>
                <p className={styles.paddedText}>{`${text}`}</p>
              </div>
            )
          })}
          {account?.tickmillCompany.id === TickmillCompaniesEnum.TICKMILL_EU && (
            <div>
              <Body1 isParagraph>
                {'3. '}
                {t('Profile.Right to request a different classification')}
              </Body1>
              <p className={styles.paddedText}>
                {t(
                  'Profile.A professional client can request to be categorised as a Retail client and obtain higher level of protection.'
                )}
              </p>
            </div>
          )}
          {account?.tickmillCompany.id === TickmillCompaniesEnum.TICKMILL_UK && (
            <div>
              <Body1 isParagraph>
                {'3. '}
                {t('Profile.FSCS Protection')}
              </Body1>
              <p className={styles.paddedText}>
                {t(
                  'Profile.You will continue to be covered by the Financial Services Compensation Scheme up to the value of £85,000 per person.'
                )}
              </p>
            </div>
          )}
          <div>
            <Body1 isParagraph>
              {'4. '}
              {t('Profile.Use of FOS')}
            </Body1>
            <p className={styles.paddedText}>
              {t('Profile.Access to the Financial Ombudsman Service (the “FOS”) is')}
            </p>
          </div>
        </Accordion>
        <Accordion
          title={t('Profile.Protections that you will lose by becoming a Professional Client')}
          id={'protection-losses-accordion'}
        >
          {[
            {
              name: t('Profile.Risk Warnings'),
              text: t(
                'Profile.We will not be obliged to restrict your account with standardised risk warnings and rules that protect Retail Clients.'
              ),
            },
            {
              name: t('Profile.Negative Balance Protection'),
              text: t(
                'Profile.The safeguards against losses exceeding the account balance, typically provided to Retail Clients, may not apply to your account.'
              ),
            },
            {
              name: t('Profile.Communications'),
              text: t(
                'Profile.When we communicate with you we may assume that your level of experience'
              ),
            },
            {
              name: t('Profile.Experience'),
              text: t(
                'Profile.We may assume your level of experience when determining if our products and services are suitable for you.'
              ),
            },
            {
              name: t('Profile.Best Execution'),
              text: t('Profile.We owe you a duty when executing your orders. As a Professional'),
            },
            {
              name: t('Profile.FOS'),
              text: t(
                'Profile.If you are classified as a professional client and do not qualify as a consumer based on the applicable definition, you may not be'
              ),
            },
            {
              name: t('Profile.No Leverage Restrictions'),
              text: t(
                'Profile.It means that you might be exposed to higher leverage that can amplify losses.'
              ),
            },
          ].map(({ name, text }, i) => {
            return (
              <div key={i}>
                <Body1 isParagraph>{`${i + 1}. ${name}`}</Body1>
                <p className={styles.paddedText}>{`${text}`}</p>
              </div>
            )
          })}
          {account?.tickmillCompany.id === TickmillCompaniesEnum.TICKMILL_EU && (
            <div>
              <Body1 isParagraph>
                {'8. '}
                {t('Profile.Investors Compensation Fund')}
              </Body1>
              <p className={styles.paddedText}>
                {t(
                  'Profile.Professional clients under Tickmill Europe (regulated by CySEC) are not entitled to a compensation by the ICF.'
                )}
              </p>
            </div>
          )}
        </Accordion>
      </>
    )
  }

  return (
    <>
      <Accordion
        title={t('Profile.Benefits of becoming a professional client')}
        id={'professional-client-benefits-accordion'}
      >
        {[
          {
            name: t('Profile.Segregated Account'),
            text: t('Profile.We will continue to segregate your funds'),
          },
          {
            name: t('Profile.Leverage & Margin'),
            text: t(
              'Profile.You will continue to benefit from the leverage / margin on our CFD and ETD products'
            ),
          },
          {
            name: t('Profile.Negative Balance Protection'),
            text: t(
              'Profile.Your account will continue to beneift from negative balance protection'
            ),
          },
        ].map(({ name, text }, i) => {
          return (
            <div key={i}>
              <Body1 isParagraph>{`${i + 1}. ${name}`}</Body1>
              <p className={styles.paddedText}>{`${text}`}</p>
            </div>
          )
        })}
      </Accordion>
      <Accordion
        title={t('Profile.Protections that you will lose by becoming a Professional Client')}
        id={'protection-losses-accordion'}
      >
        {[
          {
            name: t('Profile.Risk Warnings'),
            text: t(
              'Profile.We will not be obliged to restrict your account with standardised risk warnings and rules that protect Retail Clients.'
            ),
          },
          {
            name: t('Profile.Communications'),
            text: t(
              'Profile.When we communicate with you we may assume that your level of experience'
            ),
          },
          {
            name: t('Profile.Experience'),
            text: t(
              'Profile.We may assume your level of experience when determining if our products and services are suitable for you.'
            ),
          },
          {
            name: t('Profile.Best Execution'),
            text: t('Profile.We owe you a duty when executing your orders. As a Professional'),
          },
          {
            name: t('Profile.No Leverage Restrictions'),
            text: t(
              'Profile.It means that you might be exposed to higher leverage that can amplify losses.'
            ),
          },
        ].map(({ name, text }, i) => {
          return (
            <div key={i}>
              <Body1 isParagraph>{`${i + 1}. ${name}`}</Body1>
              <p className={styles.paddedText}>{`${text}`}</p>
            </div>
          )
        })}
      </Accordion>
    </>
  )
}

interface AccordionProps {
  title: string

  hasIconRight?: true
  id?: string
}

const Accordion: React.FunctionComponent<PropsWithChildren<AccordionProps>> = ({
  title,
  hasIconRight,
  children,
  id,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { scrollIntoView } = useScrollToElementIds()

  return (
    <div className={classNames(styles.accordion)} id={id}>
      <div
        className={classNames(styles.title, {
          [styles.justify]: hasIconRight,
        })}
        onClick={() => {
          if (!isOpen && id) {
            scrollIntoView([id])
          }
          setIsOpen(!isOpen)
        }}
      >
        <Heading6 medium>{title}</Heading6>
        <div className={styles.chevron}>
          <div className={isOpen ? styles.chevronUp : styles.chevronDown}>
            <ChevronIcon.Down />
          </div>
        </div>
      </div>
      <div
        className={classNames(styles.content, {
          [styles.open]: isOpen,
        })}
      >
        {children}
      </div>
    </div>
  )
}
